/**
 * providers/ServerAuthHintProvider.tsx
 *
 * Carries the SERVER-known login state (derived from the `is_logged_in` cookie in
 * the layout) down to deeply-nested client blocks. Unlike `UserProvider.isLoggedIn`
 * — which starts `false` on every mount and only settles after the async
 * `/api/auth/refresh` — this value is present in the very first server render AND
 * the first client render, so consumers can paint a logged-in UI on first paint
 * with no hydration mismatch and no logged-out flash.
 *
 * Defaults to `false` when no provider is mounted, so components used outside the
 * layout (or in tests) behave as logged-out.
 */

'use client';

import React, { createContext, ReactNode, useContext } from 'react';

const ServerLoggedInHintContext = createContext<boolean>(false);

export function ServerAuthHintProvider({
  value,
  children,
}: {
  value: boolean;
  children: ReactNode;
}) {
  return (
    <ServerLoggedInHintContext.Provider value={value}>
      {children}
    </ServerLoggedInHintContext.Provider>
  );
}

/** Server-known login state (from the `is_logged_in` cookie); `false` if unset. */
export function useServerLoggedInHint(): boolean {
  return useContext(ServerLoggedInHintContext);
}
